import React, { Key, memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { sectionColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import TemporaryRunTable from './TemporaryRunTable'
import { fetchEntityOptions } from '../../../widgets/static-data/api/api'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptySection } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { CustomExpandRowIcon } from '../temporary-data-table/components/CustomExpandRowIcon'
import { saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

interface TemporarySectionTableProps {
    parent: SectionDto
    data: TemporaryDataType
    setData: TemporarySetDataType
    localErrors: LocalErrorsTypes[]
    removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
}

const TemporarySectionTable = memo(
    ({
        parent,
        data,
        setData,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: TemporarySectionTableProps): ReactElement => {
        const [optionsOfSectionEntity, setOptionsOfSectionEntity] = useState(null)
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity,
            expandedRowKeys,
            setExpandedRowKeysByEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()
        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const sections = useMemo(
            () =>
                data.sections
                    .filter(sec => sec.parent == parent.nodeId)
                    .map(section => ({
                        ...section,
                        key: String(section.rowId),
                        parent: parent.nodeId
                    })),
            [data.sections, parent.nodeId]
        )

        useEffect(() => {
            fetchEntityOptions('section').then(data => setOptionsOfSectionEntity(data))

            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const section of data.sections) {
                newStartDate[section.rowId] = section.start ? new Date(section.start) : ''
                newEndDate[section.rowId] = section.end ? new Date(section.end) : ''
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        // Functions
        const handleEditData = useCallback(
            (entity: SectionDto[], row: EntityDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => String(row.rowId) === String(item.rowId))
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'SECTION').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setSections(newData)
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`sections=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: EntityDto): void => {
                handleEditData(data.sections, row, setData)
            },
            [data.sections, handleEditData, setData]
        )
        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.sections]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.start = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.sections, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.sections]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.end = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.sections, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.sections]

            const newEntity: SectionDto = {
                ...emptySection,
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'SECTION').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setSections(oldEntity)
                goToNewRow()
            })
        }, [data.sections, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: SectionDto): void => {
                const newData = data.sections.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setSections(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [checkedRows, data.sections, formatCheckedRows, removeErrorWhenCheckedRowIsRemoved, setCheckedRows, setData]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: EntityDto): ReactElement | null => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )

        const handleCalendarElementEndDate = useCallback(
            (record: EntityDto): ReactElement | null => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const sectionCols = useMemo(
            () =>
                sectionColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfSectionEntity,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.interventions,
                    localErrors
                ),
            [
                data.interventions,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                localErrors,
                moveRowToAnotherParent,
                optionsOfSectionEntity
            ]
        )

        const expandedRowRender = useCallback(
            (record: SectionDto): ReactElement => {
                return (
                    <TemporaryRunTable
                        parent={record}
                        data={data}
                        setData={setData}
                        localErrors={localErrors}
                        removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                    />
                )
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [localErrors, expandedRowKeys?.sections, removeErrorWhenCheckedRowIsRemoved, setData]
        )

        const expandIcon = useCallback(({ expanded, onExpand, record }): ReactElement => {
            return <CustomExpandRowIcon expanded={expanded} onExpand={onExpand} record={record as SectionDto} />
        }, [])

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="sections" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'sections', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: SectionDto[]) => {
                    selectAllRows({
                        data,
                        selectedRows,
                        parentNodeId: parent.nodeId,
                        type: 'sections'
                    })
                },
                onSelect: (record: EntityDto, selected: boolean, selectedRows: SectionDto[], nativeEvent: Event) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'sections',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const handleExpandedRowsChange = useCallback(
            (keys: React.Key[]): void => {
                setExpandedRowKeysByEntity('sections', keys as Key[])
            },
            [setExpandedRowKeysByEntity]
        )

        const expandable = useMemo(
            () => ({
                expandedRowRender,
                expandIcon,
                expandedRowKeys: expandedRowKeys?.sections,
                onExpandedRowsChange: handleExpandedRowsChange,
                defaultExpandAllRows: false
            }),
            [expandIcon, expandedRowKeys?.sections, expandedRowRender, handleExpandedRowsChange]
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={sections}
                    columns={sectionCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporarySectionTable.displayName = 'TemporarySectionTable'

export default TemporarySectionTable

const TableContainer = styled.main``
